import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { DatePickerHR } from "./DatePickerHR";
import api from "../lib/api";

const empty = { date: new Date().toISOString().slice(0, 10), bank_id: "", type: "placanje", currency: "EUR", amount: "", counterparty: "", category: "ostalo", status: "planirano", vehicle: "", worker_id: "", worker_name: "" };
const NO_VEHICLE = "__none__";
const NO_WORKER = "__none__";

export default function TransactionDialog({ open, onOpenChange, banks, categories, editing, onSaved, defaults }) {
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const [delayHint, setDelayHint] = useState(null);

  useEffect(() => {
    if (open) {
      setForm(editing ? { ...editing, amount: String(editing.amount), status: editing.status || "ostvareno", vehicle: editing.vehicle || "" } : { ...empty, bank_id: banks[0]?.id || "", ...(defaults || {}) });
      setDelayHint(null);
      api.get("/counterparties").then((r) => setSuggestions(r.data)).catch(() => {});
      api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
      api.get("/workers").then((r) => setWorkers(r.data.workers || [])).catch(() => {});
    }
  }, [open, editing, banks]);

  useEffect(() => {
    if (!open || form.status !== "planirano" || !form.counterparty || form.counterparty.length < 3) { setDelayHint(null); return; }
    const t = setTimeout(() => {
      api.get("/smart/delay", { params: { counterparty: form.counterparty } })
        .then((r) => setDelayHint(r.data.avg_delay != null && r.data.avg_delay >= 2 ? r.data : null))
        .catch(() => setDelayHint(null));
    }, 500);
    return () => clearTimeout(t);
  }, [open, form.counterparty, form.status]);

  const smartDate = () => {
    if (!delayHint || !form.date) return "";
    const d = new Date(form.date + "T00:00:00Z");
    d.setUTCDate(d.getUTCDate() + delayHint.avg_delay);
    return d.toISOString().slice(0, 10);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const matches = showSug && form.counterparty?.length >= 1
    ? suggestions.filter((s) => s.name.toLowerCase().includes(form.counterparty.toLowerCase()) && s.name.toLowerCase() !== form.counterparty.toLowerCase()).slice(0, 6)
    : [];

  const pickSuggestion = (s) => {
    setForm((f) => ({ ...f, counterparty: s.name, category: s.category || f.category }));
    setShowSug(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.date) { toast.error("Odaberite datum"); return; }
    setBusy(true);
    const payload = {
      ...form,
      amount: parseFloat(String(form.amount).replace(",", ".")),
      vehicle: form.vehicle || "",
      vehicle_id: vehicles.find((v) => v.plate === form.vehicle)?.id || "",
      worker_id: form.worker_id || "",
      worker_name: form.worker_name || "",
    };
    try {
      if (editing) await api.put(`/transactions/${editing.id}`, payload);
      else await api.post("/transactions", payload);
      toast.success(editing ? "Transakcija ažurirana" : "Transakcija dodana");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Greška pri spremanju");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-md" data-testid="transaction-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{editing ? "Uredi transakciju" : "Nova transakcija"}</DialogTitle>
          <DialogDescription className="sr-only">Unos transakcije</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Datum</Label>
              <DatePickerHR data-testid="tx-date-input" value={form.date} onChange={(v) => set("date", v)} className="w-full" />
            </div>
            <div className="space-y-1.5">
              <Label>Banka</Label>
              <Select value={form.bank_id} onValueChange={(v) => set("bank_id", v)}>
                <SelectTrigger data-testid="tx-bank-select"><SelectValue placeholder="Odaberi banku" /></SelectTrigger>
                <SelectContent>
                  {banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.short || b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Tip</Label>
              <Select value={form.type} onValueChange={(v) => set("type", v)}>
                <SelectTrigger data-testid="tx-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="placanje">Plaćanje (odljev)</SelectItem>
                  <SelectItem value="naplata">Naplata (priljev)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Valuta</Label>
              <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
                <SelectTrigger data-testid="tx-currency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="EUR">EUR</SelectItem>
                  <SelectItem value="BAM">BAM (KM)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Iznos</Label>
            <Input data-testid="tx-amount-input" type="number" step="0.01" min="0.01" required value={form.amount} onChange={(e) => set("amount", e.target.value)} placeholder="0.00" />
          </div>
          <div className="space-y-1.5 relative">
            <Label>Kome / od koga</Label>
            <Input
              data-testid="tx-counterparty-input"
              value={form.counterparty}
              onChange={(e) => { set("counterparty", e.target.value); setShowSug(true); }}
              onFocus={() => setShowSug(true)}
              onBlur={() => setTimeout(() => setShowSug(false), 150)}
              autoComplete="off"
              placeholder="npr. sparkasse, netto plaća..."
            />
            {matches.length > 0 && (
              <div data-testid="counterparty-suggestions" className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-[#E2E8F0] rounded-sm shadow-lg overflow-hidden">
                {matches.map((s) => (
                  <button
                    key={s.name}
                    type="button"
                    data-testid={`suggestion-${s.name}`}
                    onMouseDown={(e) => { e.preventDefault(); pickSuggestion(s); }}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-[#F1F5F9] transition-colors duration-150"
                  >
                    <span className="truncate font-medium text-[#0F172A]">{s.name}</span>
                    <span className="text-xs text-[#64748B] shrink-0">{s.category}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          {delayHint && (
            <div data-testid="smart-delay-hint" className="bg-[#D97706]/10 border border-[#D97706]/30 rounded-sm px-3 py-2 text-xs text-[#0F172A]">
              ⏱ Ovaj partner u prosjeku kasni <strong>{delayHint.avg_delay} d</strong> ({delayHint.samples} {delayHint.samples === 1 ? "plaćanje" : "plaćanja"}) — očekivana naplata <strong className="tabular-nums">{smartDate().split("-").reverse().join(".") + "."}</strong> automatski se uračunava u projekcije salda.
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Kategorija</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="tx-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="tx-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="planirano">Planirano (zadano)</SelectItem>
                  <SelectItem value="ostvareno">Ostvareno</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {vehicles.length > 0 && (
            <div className="space-y-1.5">
              <Label>Vozilo (opcionalno)</Label>
              <Select value={form.vehicle || NO_VEHICLE} onValueChange={(v) => set("vehicle", v === NO_VEHICLE ? "" : v)}>
                <SelectTrigger data-testid="tx-vehicle-select"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">
                  <SelectItem value={NO_VEHICLE}>Bez vozila</SelectItem>
                  {vehicles.map((v) => (
                    <SelectItem key={v.id} value={v.plate}>{v.plate}{v.label ? ` — ${v.label}` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-[#64748B]">Stavke označene vozilom vidljive su Rent a Car aplikaciji (od 01.09.2026.)</p>
            </div>
          )}
          {form.type === "placanje" && workers.length > 0 && (
            <div className="space-y-1.5">
              <Label>Radnik (opcionalno)</Label>
              <Select
                value={form.worker_id || NO_WORKER}
                onValueChange={(v) => {
                  const w = workers.find((x) => x.id === v);
                  setForm((f) => ({ ...f, worker_id: w ? w.id : "", worker_name: w ? w.name : "" }));
                }}
              >
                <SelectTrigger data-testid="tx-worker-select"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">
                  <SelectItem value={NO_WORKER}>Bez radnika</SelectItem>
                  {workers.map((w) => (
                    <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-[#64748B]">Ručno poveži isplatu s radnikom kad ime na izvodu nije prepoznato.</p>
            </div>
          )}
          <Button data-testid="tx-save-btn" type="submit" disabled={busy || !form.bank_id} className="w-full bg-[#0D9488] hover:bg-[#0F766E] rounded-sm">
            {busy ? "Spremanje..." : "Spremi"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
